import {
  Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Select, SelectItem, Textarea,
} from '@heroui/react';
import { Sparkles } from 'lucide-react';
import React, { FC, useState } from 'react';

import { useHeroToast } from '../../../../hooks';
import { buildDraftDocument, useGenerateTemplateDraft } from '../ai';
import type { EmailDocument } from '../model/types';

const TONES = [
  { key: 'professional', label: 'Rzeczowy' },
  { key: 'friendly', label: 'Ciepły' },
  { key: 'informative', label: 'Informacyjny' },
  { key: 'encouraging', label: 'Zachęcający' },
];

const LENGTHS = [
  { key: '80', label: 'Krótka (około 80 słów)' },
  { key: '140', label: 'Średnia (około 140 słów)' },
  { key: '220', label: 'Rozbudowana (około 220 słów)' },
];

interface AiTemplateModalProps {
  onClose: () => void;
  onReady: (doc: EmailDocument) => void;
}

/** Wiadomość z polecenia: „napisz szablon informujący o...". Układ bloków dokładamy my. */
export const AiTemplateModal: FC<AiTemplateModalProps> = ({ onClose, onReady }) => {
  const { showError } = useHeroToast();
  const { mutateAsync: generate, isPending } = useGenerateTemplateDraft();
  const [prompt, setPrompt] = useState('');
  const [tone, setTone] = useState('professional');
  const [length, setLength] = useState('140');

  const handleGenerate = async () => {
    const context = prompt.trim();
    if (context.length < 10) {
      showError('Opisz w kilku słowach, o czym ma być wiadomość.');
      return;
    }
    try {
      const draft = await generate({
        context,
        tone,
        toneLabel: TONES.find((item) => item.key === tone)?.label ?? 'Rzeczowy',
        descriptionLength: Number(length),
      });
      onReady(buildDraftDocument(context, draft));
    } catch {
      showError('Nie udało się wygenerować treści. Spróbuj jeszcze raz.');
    }
  };

  return (
    <Modal isOpen size="lg" radius="sm" onClose={onClose}>
      <ModalContent>
        <ModalHeader>Wiadomość z AI</ModalHeader>
        <ModalBody className="gap-4">
          <Textarea
            minRows={4}
            label="O czym ma być wiadomość"
            placeholder="np. poinformuj hotele o przerwie technicznej w sobotę w nocy i o tym, że zamówienia gości będą wtedy niedostępne"
            value={prompt}
            onValueChange={setPrompt}
          />
          <div className="grid grid-cols-2 gap-3">
            <Select
              label="Ton"
              selectedKeys={[tone]}
              onSelectionChange={(keys) => setTone(String(Array.from(keys)[0]))}
            >
              {TONES.map((item) => <SelectItem key={item.key}>{item.label}</SelectItem>)}
            </Select>
            <Select
              label="Długość"
              selectedKeys={[length]}
              onSelectionChange={(keys) => setLength(String(Array.from(keys)[0]))}
            >
              {LENGTHS.map((item) => <SelectItem key={item.key}>{item.label}</SelectItem>)}
            </Select>
          </div>
          <p className="text-xs text-gray-500">
            AI pisze temat i treść, a układ (logo, linia, przycisk, stopka) dokładamy my. Wszystko
            zmienisz potem w kreatorze.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="light" onPress={onClose}>Anuluj</Button>
          <Button
            size="sm"
            color="primary"
            isLoading={isPending}
            startContent={<Sparkles className="h-4 w-4" />}
            onPress={handleGenerate}
          >
            Wygeneruj
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
